import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { type FiltersUiVersion } from '@zaoblako/web/core/types/filters-ui-version.type';
import { FilterChipAutosizeHelper } from '@zaoblako/web/shared/helpers/filter-chip-autosize.helper';
import { FilterSelectChipUiHelper } from '@zaoblako/web/shared/helpers/filter-select-chip-ui.helper';
import { HighlightMatchTextHelper } from '@zaoblako/web/shared/helpers/highlight-match-text.helper';
import { OrderByPipe } from '@zaoblako/web/shared/pipes/order-by.pipe';
import { SafeHtmlPipe } from '@zaoblako/web/shared/pipes/safe-html.pipe';

import { isNil } from 'lodash';
import { SharedModule, TreeNode } from 'primeng/api';
import { FloatLabelModule } from 'primeng/floatlabel';
import { MultiSelectModule } from 'primeng/multiselect';
import { TreeFilterEvent, TreeNodeSelectEvent, TreeNodeUnSelectEvent } from 'primeng/tree';
import { TreeSelect, TreeSelectModule } from 'primeng/treeselect';

@Component({
  selector: 'crm-tree-select',
  standalone: true,
  imports: [
    CommonModule,
    MultiSelectModule,
    OrderByPipe,
    SharedModule,
    TreeSelectModule,
    FormsModule,
    SafeHtmlPipe,
    FloatLabelModule
  ],
  templateUrl: './tree-select.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.frm-filters-ui-v2]': 'filtersUiVersion === "v2"'
  }
})
export class TreeSelectComponent implements OnChanges, AfterViewInit, OnDestroy {
  @ViewChild('treeSelect') public readonly treeSelect: TreeSelect;

  public _options: TreeNode[] = [];
  public _selectedItems: TreeNode[] = [];
  public _selectedItem: TreeNode = null;
  @Output() public selectedItemsChange = new EventEmitter<any>();
  @Output() public selectedItemChange = new EventEmitter<any>();
  @Input() public selectionMode: 'single' | 'multiple' | 'checkbox' = 'single';
  @Input() public dataKey: string = 'id';
  @Input() public childrenName = 'children';
  @Input() public optionLabel = 'name';
  @Input() public placeholder = '\u00A0';
  @Input() public inputId = Math.random().toString();
  @Input() public label: string;
  @Input() public filter = true;
  @Input() public panelStyleClass: string;
  @Input() public sort = 'name:string';
  @Input() public disabled = false;
  @Input() public groupsDisabled = false;
  @Input() public filterBy = 'label';
  @Input() public emptyMessage = 'Список пуст';
  @Input() public appendTo: any;
  @Input() public scrollHeight = '300px';
  @Input() public display: 'comma' | 'chip' = 'chip';
  @Input() public maxSelectedLabels = 2;
  @Input() public selectedItemsLabel = 'Выбрано: {0}';
  @Input() public class = '';
  @Input() public expanded = true;
  @Input() public panelStyle: any;
  @Input() public metaKeySelection = false;
  @Input() public filterInputAutoFocus = true;
  @Input() public showClear = false;
  @Input() public itemStyleClass: string;
  @Input() public loading = false;

  /** V2 — оформление как у `crm-multi-select` в строке фильтров (`_filters-ui-v2.scss`). */
  @Input() public filtersUiVersion: FiltersUiVersion = 'v2';
  /**
   * null = авто: при v2 и непустом `label` — плавающая подпись; false — placeholder; true — float.
   */
  @Input() public isFloatLabel: boolean | null = null;

  @Output() public changeValue = new EventEmitter<any>();
  @Output() public panelHide = new EventEmitter<void>();
  @Output() public panelShow = new EventEmitter<void>();
  @Output() public openedChange = new EventEmitter<boolean>();
  @Output() public onFilter = new EventEmitter<TreeFilterEvent>();

  @ContentChild('valueTemplate', { static: false }) public readonly valueTemplate: TemplateRef<any>;

  private cacheSelectedItems: any[];
  private cacheSelectedItem: any;
  private chipAutosize: FilterChipAutosizeHelper | null = null;
  private viewInitialized = false;
  /** Node the user clicked; set in onNodeSelect/Unselect after ngModelChange (see finalizeFilteredCheckboxChange). */
  private lastClickedNodeKey: string | null = null;
  private lastClickedWasSelect = false;

  @Input() public set options(options: any[]) {
    if (!Array.isArray(options)) {
      this._options = [];
      return;
    }

    this._options = [];

    const parseChildren = (_options: any[]): TreeNode[] => {
      let list: TreeNode[] = [];
      if (Array.isArray(_options) && _options?.length) {
        _options
          .sort((a, b) => (a[this.sort.split(':')[0]] < b[this.sort.split(':')[0]] ? -1 : 1))
          .forEach((option: any) => {
            const children = parseChildren(option[this.childrenName]);
            list.push({
              label: option[this.optionLabel],
              key: option[this.dataKey]?.toString(),
              data: option,
              expanded: this.expanded,
              children,
              selectable: children?.length ? !this.groupsDisabled : true
            });
          });
      }
      return list;
    };

    this._options = parseChildren(options);
    if (this.selectionMode === 'single' && this.cacheSelectedItem) {
      this.selectedItem = this.cacheSelectedItem;
    } else if (
      (this.selectionMode === 'checkbox' || this.selectionMode === 'multiple') &&
      this.cacheSelectedItems?.length
    ) {
      this.selectedItems = this.cacheSelectedItems;
    }
  }

  @Input()
  public set selectedItem(selectedItem: any) {
    this._selectedItem = null;

    const parseChildren = (_options: TreeNode[]) => {
      _options.forEach((option) => {
        if (selectedItem?.[this.dataKey]?.toString() === option.key) {
          option.selectable = false;
          this._selectedItem = option;
        }
        if (this.cacheSelectedItem?.[this.dataKey]?.toString() === option.key) {
          option.selectable = option.children?.length ? !this.groupsDisabled : true;
        }
        if (option.children?.length) {
          parseChildren(option.children);
        }
      });
    };
    parseChildren(this._options);
    this.cacheSelectedItem = selectedItem;
    this.refreshChipAutosize();
  }

  @Input()
  public set selectedItems(selectedItems: any[]) {
    if (!Array.isArray(selectedItems)) {
      return;
    }

    this._selectedItems = [];

    const parseChildren = (_options: TreeNode[], parent?: TreeNode) => {
      _options.forEach((option) => {
        if (selectedItems.findIndex((si) => si[this.dataKey]?.toString() === option.key) !== -1) {
          this._selectedItems.push(option);
        }
        if (option.children?.length) {
          parseChildren(option.children, option);
        }
      });
      if (
        parent?.children?.every(
          (child) => this._selectedItems.findIndex((selectItem) => selectItem.key === child.key) !== -1
        ) &&
        this._selectedItems.findIndex((selectItem) => selectItem.key === parent?.key) === -1
      ) {
        this._selectedItems.push(parent);
      }
      if (
        !parent?.children?.every(
          (child) => this._selectedItems.findIndex((selectItem) => selectItem.key === child.key) !== -1
        ) &&
        this._selectedItems.findIndex((selectItem) => selectItem.key === parent?.key) !== -1
      ) {
        this._selectedItems = this._selectedItems.filter((selectItem) => selectItem.key !== parent?.key);
      }
    };

    parseChildren(this._options);
    this.cacheSelectedItems = selectedItems;
    this.refreshChipAutosize();
  }

  public constructor(
    private readonly cdRef: ChangeDetectorRef,
    private readonly elementRef: ElementRef<HTMLElement>
  ) {}

  public get mergedPanelStyleClass(): string {
    return FilterSelectChipUiHelper.mergePanelStyleClass(this.panelStyleClass, this.filtersUiVersion);
  }

  public usesFloatLabel(): boolean {
    return FilterSelectChipUiHelper.usesFloatLabel({
      isFloatLabel: this.isFloatLabel,
      filtersUiVersion: this.filtersUiVersion,
      label: this.label
    });
  }

  public inputPlaceholder(): string {
    return FilterSelectChipUiHelper.inputPlaceholder({
      usesFloatLabel: this.usesFloatLabel(),
      label: this.label,
      placeholder: this.placeholder
    });
  }

  public get effectiveMaxSelectedLabels(): number {
    return FilterSelectChipUiHelper.effectiveMaxSelectedLabels({
      isFilterChipMode: this.shouldUseChipAutosize(),
      maxSelectedLabels: this.maxSelectedLabels
    });
  }

  public get treeSelectDisplay(): 'comma' | 'chip' {
    return FilterSelectChipUiHelper.chipOrCommaDisplay({
      display: this.display,
      selectedCount: this._selectedItems?.length ?? 0,
      maxLabels: this.effectiveMaxSelectedLabels,
      selectionMode: this.selectionMode
    }) as 'comma' | 'chip';
  }

  public formatSelectedItemsLabel(count: number): string {
    return FilterSelectChipUiHelper.formatSelectedItemsLabel(this.selectedItemsLabel, count);
  }

  public ngOnChanges() {
    const updateItem = (options: TreeNode[]) => {
      options.forEach((opt) => {
        opt.selectable = opt.children?.length ? !this.groupsDisabled : true;
        if (this.selectionMode === 'single' && this._selectedItem?.key === opt.key) {
          opt.selectable = false;
        }
        if (opt.children?.length) {
          updateItem(opt.children);
        }
      });
    };

    updateItem(this._options);

    if (this.viewInitialized) {
      this.setupChipAutosize();
      this.refreshChipAutosize();
    }
  }

  public ngAfterViewInit(): void {
    this.viewInitialized = true;
    this.setupChipAutosize();
    this.refreshChipAutosize();
  }

  public ngOnDestroy(): void {
    this.chipAutosize?.disconnect();
    this.chipAutosize = null;
  }

  public onChangeValue(event: any) {
    if (this.selectionMode === 'single') {
      this.changeValue.emit(event?.data);
      this.selectedItemChange.emit(event?.data);
      this.clearSearchFilter();
      this.refreshChipAutosize();
      return;
    }

    // Under an active filter, wait for onNodeSelect/Unselect so we know what was clicked.
    // PrimeNG may auto-add parents via propagateUp when only filtered children are visible.
    if (this.selectionMode === 'checkbox' && this.isSearchFilterActive()) {
      queueMicrotask(() => this.finalizeFilteredCheckboxChange());
      return;
    }

    const selection: TreeNode[] = event || [];
    const selectedData = selection.map((selectItem) => selectItem.data);
    this.changeValue.emit(selectedData);
    this.selectedItemsChange.emit(selectedData);
    this.clearSearchFilter();
    this.refreshChipAutosize();
  }

  public onPanelShow(): void {
    this.panelShow.emit();
    this.openedChange.emit(true);
  }

  public onPanelHide(): void {
    this.clearSearchFilter();
    this.panelHide.emit();
    this.openedChange.emit(false);
    this.syncHiddenInputFocusAfterOverlayClose();
    this.refreshChipAutosize();
  }

  /**
   * После закрытия overlay фокус часто остаётся на скрытом combobox (Prime onOverlayBeforeHide)
   * или на поле фильтра в панели — `focused` не сбрасывается, остаётся `.p-focus` и «активная» рамка v2.
   */
  private syncHiddenInputFocusAfterOverlayClose(): void {
    const blurTreeSelectInternals = (): void => {
      const active = document.activeElement as HTMLElement | null;
      if (active?.classList?.contains('p-treeselect-filter')) {
        active.blur();
      }

      const focusNative = this.getTreeSelectFocusNativeInput();
      if (focusNative && !this.disabled) {
        focusNative.blur();
      }

      this.cdRef.detectChanges();
    };

    setTimeout(blurTreeSelectInternals, 0);
  }

  private getTreeSelectFocusNativeInput(): HTMLInputElement | null {
    const ts = this.treeSelect as TreeSelect & { focusInput?: { nativeElement?: HTMLInputElement; } };
    const fromRef = ts?.focusInput?.nativeElement;
    if (fromRef) {
      return fromRef;
    }
    if (typeof document === 'undefined' || !this.inputId) {
      return null;
    }
    return document.getElementById(this.inputId) as HTMLInputElement | null;
  }

  public filterValue(event: TreeFilterEvent) {
    this.onFilter.emit(event);
  }

  public highlightMatchText(text: string): string {
    return HighlightMatchTextHelper.highlightMatchText(text, this.treeSelect?.filterValue);
  }

  public onNodeSelect(event: TreeNodeSelectEvent): void {
    this.lastClickedNodeKey = !isNil(event.node?.key) ? String(event.node.key) : null;
    this.lastClickedWasSelect = true;
  }

  public onNodeUnselect(event: TreeNodeUnSelectEvent) {
    this.lastClickedNodeKey = !isNil(event.node?.key) ? String(event.node.key) : null;
    this.lastClickedWasSelect = false;

    const parseChildren = (_options: TreeNode[]) => {
      _options.forEach((option) => {
        if (event.node?.key === option.key && option.children?.length && !option.expanded) {
          option.expanded = true;
        }

        if (event.node?.key === option.key && event.node.parent && !event.node.parent?.expanded) {
          option.parent.expanded = true;
        }
        if (option.children?.length) {
          parseChildren(option.children);
        }
      });
    };

    setTimeout(() => {
      parseChildren(this._options);
      this._options = [...this._options];
      this.cdRef.markForCheck();
    });
  }

  public removeSelectedItem = (selectedItem: TreeNode, event?: Event): void => {
    if (!selectedItem) {
      return;
    }

    event?.stopPropagation();
    event?.preventDefault();

    const keysToRemove = this.collectNodeKeys(selectedItem);
    const filteredItems = this._selectedItems
      .map((item) => item.data)
      .filter((item) => !keysToRemove.has(item?.[this.dataKey]?.toString()));

    this.selectedItems = filteredItems;
    this.onChangeValue(this._selectedItems);
    this.cdRef.markForCheck();
    this.refreshChipAutosize();
  };

  private collectNodeKeys(node: TreeNode, keys = new Set<string>()): Set<string> {
    if (!isNil(node?.key)) {
      keys.add(String(node.key));
    }
    node?.children?.forEach((child) => this.collectNodeKeys(child, keys));
    return keys;
  }

  private collectNodes(node: TreeNode, nodes: TreeNode[] = []): TreeNode[] {
    if (!node) {
      return nodes;
    }
    nodes.push(node);
    node.children?.forEach((child) => this.collectNodes(child, nodes));
    return nodes;
  }

  private findNodeByKey(nodes: TreeNode[], key: string): TreeNode | null {
    for (const node of nodes) {
      if (String(node.key) === key) {
        return node;
      }
      if (node.children?.length) {
        const found = this.findNodeByKey(node.children, key);
        if (found) {
          return found;
        }
      }
    }
    return null;
  }

  private isSearchFilterActive(): boolean {
    return !!this.treeSelect?.filterValue?.length;
  }

  /**
   * After checkbox change under an active filter:
   * - click on a group → select/deselect all original descendants (PrimeNG only sees filtered children);
   * - click on a leaf → drop parents that propagateUp added because filtered siblings were hidden.
   */
  private finalizeFilteredCheckboxChange(): void {
    let selection = [...(this._selectedItems || [])];
    const clickedKey = this.lastClickedNodeKey;
    const wasSelect = this.lastClickedWasSelect;
    this.lastClickedNodeKey = null;

    if (clickedKey) {
      const original = this.findNodeByKey(this._options, clickedKey);
      if (original?.children?.length) {
        const selectionByKey = new Map<string, TreeNode>();
        for (const node of selection) {
          if (!isNil(node?.key)) {
            selectionByKey.set(String(node.key), node);
          }
        }
        if (wasSelect) {
          this.collectNodes(original).forEach((node) => {
            if (!isNil(node?.key)) {
              selectionByKey.set(String(node.key), node);
            }
          });
        } else {
          this.collectNodeKeys(original).forEach((key) => selectionByKey.delete(key));
        }
        selection = [...selectionByKey.values()];
      } else {
        selection = this.stripIncompleteParents(selection);
      }
    } else {
      selection = this.stripIncompleteParents(selection);
    }

    this._selectedItems = selection;
    const selectedData = selection.map((node) => node.data);
    this.changeValue.emit(selectedData);
    this.selectedItemsChange.emit(selectedData);
    this.clearSearchFilter();
    this.refreshChipAutosize();
    this.cdRef.markForCheck();
  }

  /** Remove group nodes that are not fully selected in the original (unfiltered) tree. */
  private stripIncompleteParents(selection: TreeNode[]): TreeNode[] {
    const keys = new Set(selection
        .filter((node) => !isNil(node?.key))
        .map((node) => String(node.key)));
    return selection.filter((node) => {
      if (isNil(node?.key)) {
        return false;
      }
      const original = this.findNodeByKey(this._options, String(node.key));
      if (!original?.children?.length) {
        return true;
      }
      return original.children.every((child) => keys.has(String(child.key)));
    });
  }

  private clearSearchFilter(): void {
    if (!this.isSearchFilterActive()) {
      return;
    }

    this.treeSelect.filterValue = null;
    this.treeSelect.filteredNodes = null;
    this.treeSelect.treeViewChild?.resetFilter();
    this.onFilter.emit({ filter: '', filteredValue: null });
    this.cdRef.markForCheck();
  }

  private shouldUseChipAutosize(): boolean {
    return FilterSelectChipUiHelper.isFilterChipMode({
      filtersUiVersion: this.filtersUiVersion,
      usesFloatLabel: this.usesFloatLabel(),
      label: this.label
    });
  }

  private setupChipAutosize(): void {
    this.chipAutosize?.disconnect();
    this.chipAutosize = null;

    if (!this.shouldUseChipAutosize()) {
      return;
    }

    this.chipAutosize = new FilterChipAutosizeHelper(this.elementRef.nativeElement, {
      resolveMeasureText: () => this.getChipAutosizeMeasureText()
    });
    this.chipAutosize.connect();
  }

  private getChipAutosizeMeasureText(): string | null {
    const count = this._selectedItems?.length ?? 0;
    return FilterSelectChipUiHelper.resolveChipAutosizeMeasureText({
      selectedCount: count,
      maxLabels: this.effectiveMaxSelectedLabels,
      selectedItemsLabel: this.selectedItemsLabel,
      isChipDisplay: this.display === 'chip' && this.selectionMode !== 'single',
      singleItemLabel: this._selectedItems[0]?.label?.toString() ?? null
    });
  }

  private refreshChipAutosize(): void {
    this.chipAutosize?.scheduleSync();
  }
}
